import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import useReveal from "../hooks/useReveal";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const engagements = [
  {
    n: "01",
    name: "Diagnose",
    heading: "Something isn't working. Let's understand why.",
    body: "A focused, fixed-scope engagement to understand the business, identify what's really holding things back, and establish what needs to happen next.",
    youGet: "Diagnosis, priorities and a clear roadmap.",
    engagement: "Fixed scope.",
    cta: "Start here",
    accent: "#F2C14E",
    fg: "#1C1C19",
  },
  {
    n: "02",
    name: "Consult",
    heading: "You need someone senior in the room.",
    body: "Ongoing strategic support for founders and leadership teams who need experienced marketing thinking, decision support and someone to help keep things moving.",
    youGet: "Strategy, perspective and ongoing decision support.",
    engagement: "Ongoing.",
    cta: "Talk to us",
    accent: "#E26D5C",
    fg: "#F7F5F0",
  },
  {
    n: "03",
    name: "Growth Partner",
    heading: "You want someone to take it forward.",
    body: "GroundWork stays close to the work and leads the growth agenda, bringing together the right specialists across brand, creative, performance and growth.",
    youGet: "Strategy, execution and accountability.",
    engagement: "Ongoing.",
    cta: "Build with us",
    accent: "#729B79",
    fg: "#F7F5F0",
  },
];

export default function Approach() {
  useReveal();
  return (
    <main className="min-h-screen bg-[#F7F5F0] text-[#1C1C19] overflow-x-hidden">
      <Navbar />

      {/* Opening */}
      <section
        data-testid="approach-hero"
        className="bg-[#F7F5F0] pt-32 md:pt-48 pb-16 md:pb-24"
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-3 reveal">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#5C5C58]">
              Approach
            </span>
            <div className="mt-6 w-12 h-px bg-[#1C1C19]" />
          </div>
          <div className="md:col-span-9 reveal">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-light tracking-tight leading-[1.05] text-[#1C1C19]">
              Start with the problem.{" "}
              <span className="italic text-[#E26D5C]">
                Choose how far you want us involved.
              </span>
            </h1>
            <p className="mt-8 max-w-3xl text-lg md:text-xl font-light leading-relaxed text-[#1C1C19]/80">
              You don't need to know exactly what you need before you talk to
              us. Bring us the problem. We'll understand the business, work out
              what actually needs to happen, and figure out the right level of
              involvement.
            </p>
          </div>
        </div>
      </section>

      {/* Engagements */}
      <section className="bg-[#F7F5F0] py-8 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 space-y-8 md:space-y-10">
          {engagements.map((e) => (
            <div
              key={e.n}
              data-testid={`approach-engagement-${e.n}`}
              className="reveal grid md:grid-cols-12 rounded-3xl overflow-hidden border border-[#D8D4C7] transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
            >
              {/* Accent panel */}
              <div
                className="md:col-span-4 p-8 md:p-10 flex flex-col justify-between gap-8"
                style={{ backgroundColor: `${e.accent}22` }}
              >
                <div>
                  <span
                    className="font-display text-7xl md:text-8xl font-light leading-none"
                    style={{ color: e.accent }}
                  >
                    {e.n}
                  </span>
                  <p className="mt-5 font-display text-3xl md:text-4xl font-light tracking-tight text-[#1C1C19]">
                    {e.name}
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#1C1C19]/50">
                      You get
                    </div>
                    <p className="mt-2 text-sm font-light text-[#1C1C19]/80">
                      {e.youGet}
                    </p>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#1C1C19]/50">
                      Engagement
                    </div>
                    <p className="mt-2 text-sm font-light text-[#1C1C19]/80">
                      {e.engagement}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="md:col-span-8 p-8 md:p-12 bg-white flex flex-col">
                <div
                  className="w-12 h-1 rounded-full mb-8"
                  style={{ backgroundColor: e.accent }}
                />
                <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight leading-[1.12] text-[#1C1C19]">
                  {e.heading}
                </h2>
                <p className="mt-6 text-lg font-light leading-relaxed text-[#1C1C19]/80 flex-1">
                  {e.body}
                </p>
                <Link
                  to="/#contact"
                  data-testid={`approach-cta-${e.n}`}
                  className="btn-magnetic mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full text-sm font-semibold uppercase tracking-widest self-start"
                  style={{ backgroundColor: e.accent, color: e.fg }}
                >
                  {e.cta}
                  <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section
        data-testid="approach-closing"
        className="bg-[#F7F5F0] py-24 md:py-32 border-t border-[#D8D4C7]"
      >
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl reveal">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#1C1C19]">
              You don't have to commit to{" "}
              <span className="italic text-[#729B79]">all three.</span>
            </h2>
            <p className="mt-6 text-lg font-light leading-relaxed text-[#1C1C19]/80">
              Start with what you need today. If the work calls for something
              more, we'll figure that out together.
            </p>
            <Link
              to="/#contact"
              data-testid="approach-closing-cta"
              className="btn-magnetic mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#1C1C19] text-[#F7F5F0] text-sm font-semibold uppercase tracking-widest hover:bg-[#E26D5C]"
            >
              Build with us
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
